import React from "react";
import Axios from "axios";
import { BlockSquare } from "../../components/common/StyledComponents";
import {
  antdAsyncMessageError,
  antdAsyncMessageLoading,
  antdAsyncMessageSuccess,
} from "../../helpers/AsyncMessage";
import {
  antdMessageError,
  buildErrorObject,
  errorHandler,
  prettyDateFormat,
} from "../../helpers/Utils";
import { DEFAULT_IMUM_PER_PAGE } from "../../helpers/default";

export const getImums =
  (page, filterParams, type = "Imum") =>
  async (dispatch, getState) => {
    let cancel;
    try {
      dispatch({ type: "ONLY_LOADING_CUSTOM" });
      const func_api_call_time = Date.now();
      const { data } = await Axios({
        method: "GET",
        url: `/api/v1/${window.identifier}/imums.json?page=${page}&per_page=${
          type == "Imum" ? DEFAULT_IMUM_PER_PAGE : 9999
        }${filterParams}`,
        cancelToken: new Axios.CancelToken((c) => (cancel = c)),
      });
      const full_data =
        page == 1 ? data.data : [...getState().custom.data, ...data.data];
      const full_data_ids = [...new Set(full_data.map((d) => d.id))];
      const full_filtered = full_data_ids.map((d) =>
        full_data.find((s) => s.id == d)
      );

      dispatch(
        setValue(
          {
            type: "GET_CUSTOMS",
            payload: {
              ...data,
              api_call_time: func_api_call_time,
              data: full_filtered,
              current: type,
              show_data: {
                last_sync: data.last_sync
                  ? prettyDateFormat(data.last_sync)
                  : "",
                progress: data.progress,
                last_sync_priority: data.last_sync_priority
                  ? prettyDateFormat(data.last_sync_priority)
                  : "",
                progress_priority: data.progress_priority,
              },
            },
          },
          func_api_call_time
        )
      );
      return {
        success: true,
        has_more:
          data?.data?.length == 0
            ? false
            : data?.data?.length % DEFAULT_IMUM_PER_PAGE == 0,
      };
    } catch (err) {
      errorHandler(err);
      if (Axios.isCancel(err)) return;
      antdMessageError();
      return { success: false };
    }
  };

export const getImumsOptions = () => async (dispatch) => {
  try {
    const { data } = await Axios.get(
      `/api/v1/${window.identifier}/imums/imum_shops`
    );
    const { data: imum_product_tags } = await Axios.get(
      `/api/v1/${window.identifier}/imum_product_tags`
    );
    const { data: imum_rules = [] } = await Axios.get(
      `/api/v1/${window.identifier}/imum_rules/options`
    );

    if (!data.success) return;
    // console.log(data.data);
    dispatch({
      type: "SET_OPTIONS",
      payload: {
        shops: data.data.map((d) => ({
          value: d,
          label: d,
        })),
        imum_product_tags: imum_product_tags.map((d) => ({
          value: d.id,
          label: d.title,
          color: d.color,
          preview: (
            <div className="d-flex align-items-center">
              <div className="mr-1">
                <BlockSquare color={d.color} />
              </div>
              {d.title}
            </div>
          ),
        })),
        imum_rules,
      },
    });
    return { success: true };
  } catch (err) {
    console.log("ERROR ", err);
    return { success: false };
  }
};

export const rollbackImumToPim = async (id) => {
  try {
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/rollback_pim?id=${id}`
    );
    antdAsyncMessageLoading({ key: "export_to_pim" });

    if (!data.success) throw new Error("ERR");
    antdAsyncMessageSuccess({ key: "export_to_pim" });
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "export_to_pim" });
    return { success: false };
  }
};

export const exportImumToAlso = (lang, type, ids, filterString) => async () => {
  try {
    antdAsyncMessageLoading({ key: "export_to_pim", text: "Sent to import" });

    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/export_to_pim?${filterString}`,
      {
        all: type == "all" ? true : false,
        ids,
        lang:
          window.identifier == "technorama" &&
          lang.length == 1 &&
          lang[0] == "LT"
            ? ["TCH"]
            : lang,
      }
    );
    if (!data.success) throw new Error("ERR");

    setTimeout(() => {
      antdAsyncMessageSuccess({ key: "export_to_pim" });
    }, 3000);
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "export_to_pim" });

    return { success: false };
  }
};

export const imumMassChangePriceToOldPrice = async (lang, ids) => {
  const key = "mass_change_price";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/mass_change_price`,
      {
        ids,
        lang,
      }
    );
    if (!data.success) throw new Error("ERR");
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumDisableChannelPriceScope = async (lang, ids) => {
  const key = "disable_channel_price_scope";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/disable_channel_price_scope`,
      {
        ids,
        lang,
      }
    );
    if (!data.success) throw new Error("Operation failed");
    if (data.success) antdAsyncMessageSuccess({ key });
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumDisableChannelPriceScopeParams = async (lang, queryString) => {
  const key = "disable_channel_price_scope";

  const transformQuery = (queryStr) => {
    const cleanStr = queryStr.startsWith("&")
      ? queryStr.substring(1)
      : queryStr;

    const params = {};
    const matches = cleanStr.match(/q\[([^\]]+)\](\[\])?=([^&]+)/g);

    if (matches) {
      matches.forEach((match) => {
        const [_, param, isArray, value] = match.match(
          /q\[([^\]]+)\](\[\])?=([^&]+)/
        );
        if (isArray) {
          if (!params[param]) {
            params[param] = [];
          }
          params[param].push(value.toLowerCase());
        } else {
          params[param] = value.toLowerCase();
        }
      });
    }

    return { q: params };
  };

  try {
    antdAsyncMessageLoading({ key });
    const transformedQuery = transformQuery(queryString);
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/disable_channel_price_scope`,
      {
        lang,
        ...transformedQuery,
      }
    );

    if (!data.success) throw new Error("Operation failed");
    if (data.success) antdAsyncMessageSuccess({ key });
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const imumMassNewPriceChangeByCountries = async (
  filterString,
  variables
) => {
  const key = "mass_new_price_change_by_countries";
  try {
    antdAsyncMessageLoading({ key });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/mass_new_price_change_by_countries?${filterString}`,
      variables
    );
    if (!data.success) throw new Error("ERR");
    return { success: true };
  } catch (err) {
    antdAsyncMessageError({ key });
    errorHandler(err);
    return { success: false };
  }
};

export const getImumXls = async (params) => {
  try {
    antdAsyncMessageLoading({
      key: "exportDocument",
      text: <span id="imum_xlsx_progress">In progress...</span>,
    });
    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/get_xls?${params?.filter || ""}`,
      params
    );
    if (!data.success) throw new Error("ERR");
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "exportDocument" });
  }
};

export const putImums =
  (id, imum, parent_id, related_ids) => async (dispatch) => {
    try {
      antdAsyncMessageLoading({ key: "putImums" });

      const { data } = await Axios({
        url: `/api/v1/${window.identifier}/imums/${id}.json`,
        data: {
          imum,
          parent_id,
          related_ids,
        },
        method: "PUT",
      });
      // if (!data.success) throw new Error("ERR");
      antdAsyncMessageSuccess({ key: "putImums" });
      dispatch({
        type: "UPDATE_CUSTOM",
        payload: data,
      });
      return { success: true };
    } catch (err) {
      errorHandler(err);
      antdAsyncMessageError({ key: "putImums" });
      return {
        success: false,
        errors: buildErrorObject(err?.response?.data?.errors),
      };
    }
  };

export const putImumProduct = (body) => async (dispatch) => {
  try {
    antdAsyncMessageLoading({ key: "putImumProduct" });
    const { data } = await Axios({
      url: `/api/v1/${window.identifier}/imums/${body.id}.json`,
      data: {
        imum_product: body,
      },
      method: "put",
    });
    antdAsyncMessageSuccess({ key: "putImumProduct" });
    console.log({ data });
    dispatch({
      type: "UPDATE_CUSTOM",
      payload: data,
    });
    return { success: true };
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "putImumProduct" });
    return {
      success: false,
      errors: buildErrorObject(err?.response?.data?.errors),
    };
  }
};

export const imumCheckIfHaveDifference = async (params) => {
  try {
    antdAsyncMessageLoading({
      key: "imumCheckIfHaveDifference",
      text: "Check if have prices 15% difference",
    });

    const { data } = await Axios.post(
      `/api/v1/${window.identifier}/imums/check_if_have_15_difference`,
      {
        ...params,
        lang:
          window.identifier == "technorama" &&
          params.lang.length == 1 &&
          params.lang[0] == "LT"
            ? ["TCH"]
            : params.lang,
      }
    );

    if (data.success) {
      antdAsyncMessageSuccess({
        key: "imumCheckIfHaveDifference",
      });
      return data;
    } else {
      antdAsyncMessageSuccess({
        key: "imumCheckIfHaveDifference",
      });
      return data;
    }
    throw new Error("ERR");
  } catch (err) {
    errorHandler(err);
    antdAsyncMessageError({ key: "imumCheckIfHaveDifference" });
    return false;
  }
};

const setValue =
  (val, time = Date.now()) =>
  (dispatch, getState) => {
    const api_call_time = getState().custom.api_call_time;
    console.log("1");
    if (api_call_time > time) return console.log("%cSkipina", "color: red;");
    console.log("3");
    dispatch(val);
  };
